import type { MutationOptions } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  createDmsScheduleIntegrationRequest,
  getDmsScheduleAccounts,
  getDmsScheduleAutomationStatus,
} from 'api/dmsScheduleAccounts';
import { RequestQueryKeysE, RequestStaleTimeE } from 'declarations/enums/react-query';
import type { DmsScheduleAccountsI } from 'declarations/interfaces/dmsScheduleAccounts';
import type { DmsScheduleAutomationDto } from 'declarations/interfaces/DmsScheduleAutomationDto';
import type { ApiErrorI } from 'declarations/interfaces/errors';

type ScheduleAccountsRequestT = {
  isEnabled: boolean;
  roofTopId: string | undefined;
};

type useCreateDmsScheduleIntegrationRequestParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, string>['onSuccess'];
};

export const useFetchDmsScheduleAccounts = (
  params: ScheduleAccountsRequestT
): ReturnType<typeof useQuery<DmsScheduleAccountsI[], ApiErrorI>> => {
  return useQuery<DmsScheduleAccountsI[], ApiErrorI>(
    [RequestQueryKeysE.FetchDmsScheduleAccounts, params.roofTopId],
    (): Promise<DmsScheduleAccountsI[]> => getDmsScheduleAccounts(params.roofTopId),
    {
      enabled: params.isEnabled,
      staleTime: RequestStaleTimeE.TicketRequestCategories,
    }
  );
};

export const useFetchDmsScheduleAutomation = (
  roofTopId: string
): ReturnType<typeof useQuery<DmsScheduleAutomationDto, ApiErrorI>> => {
  return useQuery<DmsScheduleAutomationDto, ApiErrorI>(
    [RequestQueryKeysE.FetchDmsScheduleAutomationStatus, roofTopId],
    (): Promise<DmsScheduleAutomationDto> => getDmsScheduleAutomationStatus(roofTopId),
    {
      enabled: roofTopId !== undefined,
      staleTime: RequestStaleTimeE.TicketRequestCategories,
    }
  );
};

export const useCreateDmsScheduleIntegrationRequest = ({
  onSuccess,
}: useCreateDmsScheduleIntegrationRequestParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, string>
> => {
  return useMutation<void, ApiErrorI, string>(
    [RequestQueryKeysE.CreateDmsScheduleIntegrationRequest],
    (roofTopId: string): Promise<void> => createDmsScheduleIntegrationRequest(roofTopId),
    { onSuccess }
  );
};
